import { Fade, Stack } from '@mui/material'
import {
  PerpOrderRecord,
  PerpPositionClearingsRecord,
  PerpPositionRecord,
} from 'api/v1/perp'
import { SpotMatchRecord, SpotOrderRecord } from 'api/v1/spot'
import { atom, useAtomValue } from 'jotai'
import { useSetAtom } from 'jotai/index'
import { memo, NamedExoticComponent, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useModal } from 'store/app/hooks'
import { ModalMask } from 'styles'
import { ModalTitle } from './ModalTitle'
import { PerpClearingHistoryModal } from './PerpClearingHistory'
import { PerpOrderHistoryModal } from './PerpOrderHistory'
import { PerpPositionModal } from './PerpPosition'
import { SpotOrderHistoryModal } from './SpotOrderHistory'
import { SpotTradeHistoryModal } from './SpotTradeHistory'
import { OrderMainWrap, OrderModalWrap } from './styles'
import { updateModal } from '../../store/app/actions/modalActions'

export enum OrderModalType {
  SpotOpenOrder,
  SpotOrderHistory,
  SpotTradeHistory,
  PerpPosition,
  PerpOpenOrder,
  PerpOrderHistory,
  PerpTradeHistory,
}

export type OrderModalData =
  | PerpPositionRecord
  | SpotOrderRecord
  | SpotMatchRecord
  | PerpOrderRecord
  | PerpPositionClearingsRecord

export const orderModalAtom = atom<
  | {
      orderType: OrderModalType
      data: OrderModalData
    }
  | undefined
>(undefined)

export const orderModalTitleAtom = atom<string>('')

export function useUpdateModalTitle(symbolName: string) {
  const updateModalTitle = useSetAtom(orderModalTitleAtom)
  useEffect(() => {
    updateModalTitle(symbolName ?? '')
    return () => {
      updateModalTitle('')
    }
  }, [symbolName])
}

export function useCloseOrderModal() {
  const dispatch = useDispatch()
  return useCallback(() => {
    dispatch(
      updateModal({
        modal: 'mobileUserOrder',
        open: false,
      })
    )
  }, [])
}

const Modals: Record<
  OrderModalType,
  NamedExoticComponent<{
    openOrderCancel?: ((orderItem: OrderModalData) => void) | undefined
  }>
> = {
  [OrderModalType.SpotOpenOrder]: SpotOrderHistoryModal,
  [OrderModalType.SpotOrderHistory]: SpotOrderHistoryModal,
  [OrderModalType.SpotTradeHistory]: SpotTradeHistoryModal,
  [OrderModalType.PerpPosition]: PerpPositionModal,
  [OrderModalType.PerpOpenOrder]: PerpOrderHistoryModal,
  [OrderModalType.PerpOrderHistory]: PerpOrderHistoryModal,
  [OrderModalType.PerpTradeHistory]: PerpClearingHistoryModal,
}

export const MobileUserOrderModal = memo<{
  openOrderCancel?: (orderItem: OrderModalData) => void
}>(({ openOrderCancel }) => {
  const openMobileUserOrderModal = useModal('mobileUserOrder')
  const { orderType } = useAtomValue(orderModalAtom) ?? {}

  const onClose = useCloseOrderModal()

  if (orderType === undefined || orderType in OrderModalType === false) {
    return null
  }

  const ModalView = Modals[orderType]

  return (
    <Fade in={openMobileUserOrderModal} timeout={200}>
      <OrderModalWrap>
        <ModalMask onClick={onClose}></ModalMask>
        <OrderMainWrap>
          <ModalTitle />
          <Stack>
            <ModalView openOrderCancel={openOrderCancel} />
          </Stack>
        </OrderMainWrap>
      </OrderModalWrap>
    </Fade>
  )
})
